import React, { useState, useMemo } from "react";
import TableCard from "../../shared/TableCard/TableCard";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { Chips } from "primereact/chips";
import { useDispatch } from "react-redux";
import {
    getNetworkGasFeeList,
    createNetworkGasFee,
    updateNetworkGasFee,
    toggleNetworkGasFee,
    deleteNetworkGasFee,
} from "../../redux/actions/networkGasFeeAction";

const DEFAULT_FORM = {
    network: "",
    label: "",
    gasFee: 0,
    supportedCurrencies: [],
    isActive: true,
};

export default function NetworkGasFeesTable({ heading, networkGasFees }) {
    const [loading, setLoading] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editing, setEditing] = useState(null); // null = create, object = edit
    const [form, setForm] = useState(DEFAULT_FORM);

    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [deleteError, setDeleteError] = useState("");

    const dispatch = useDispatch();

    const sortedNetworks = useMemo(() => {
        return [...(networkGasFees || [])].sort((a, b) => a.network.localeCompare(b.network));
    }, [networkGasFees]);

    const openCreate = () => {
        setEditing(null);
        setForm(DEFAULT_FORM);
        setDialogVisible(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            network: row.network,
            label: row.label,
            gasFee: row.gasFee,
            supportedCurrencies: [...(row.supportedCurrencies || [])],
            isActive: row.isActive,
        });
        setDialogVisible(true);
    };

    const handleSubmit = () => {
        if (!form.network || !form.label) {
            return; // primitive client-side guard; backend enforces too
        }
        if (editing) {
            // Don't allow renaming the network key
            const { network, ...payload } = form;
            dispatch(updateNetworkGasFee(editing._id, payload, setLoading));
        } else {
            dispatch(createNetworkGasFee(form, setLoading));
        }
        setDialogVisible(false);
        setEditing(null);
    };

    const handleToggle = (row) => {
        dispatch(toggleNetworkGasFee(row._id, setLoading));
    };

    const handleDeleteClick = (row) => {
        setDeleteTarget(row);
        setDeleteConfirmText("");
        setDeleteError("");
        setDeleteDialogVisible(true);
    };

    const handleDeleteConfirm = () => {
        if (deleteConfirmText !== "DELETE") {
            setDeleteError('Please type "DELETE" to confirm');
            return;
        }
        dispatch(deleteNetworkGasFee(deleteTarget._id, setLoading));
        setDeleteDialogVisible(false);
        setDeleteTarget(null);
        setDeleteConfirmText("");
    };

    const currenciesTemplate = (rowData) => {
        const list = rowData.supportedCurrencies || [];
        if (list.length === 0) return <span style={{ opacity: 0.5 }}>-</span>;
        return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                {list.map((c) => (
                    <span
                        key={c}
                        style={{
                            background: "#2a2a3a",
                            color: "#08eefe",
                            padding: "0.15rem 0.5rem",
                            borderRadius: "0.25rem",
                            fontSize: "0.8rem",
                        }}
                    >
                        {c}
                    </span>
                ))}
            </div>
        );
    };

    const gasFeeTemplate = (rowData) => {
        return <span>{rowData.gasFee?.toFixed(2) ?? "0.00"}</span>;
    };

    const activeTemplate = (rowData) => {
        return (
            <InputSwitch
                checked={rowData.isActive}
                onChange={() => handleToggle(rowData)}
                tooltip={rowData.isActive ? "Active — visible to users" : "Inactive — hidden from users"}
                tooltipOptions={{ position: "left" }}
            />
        );
    };

    const actionsTemplate = (rowData) => {
        return (
            <div style={{ display: "flex", gap: "0.25rem" }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text"
                    onClick={() => openEdit(rowData)}
                    tooltip="Edit network"
                    tooltipOptions={{ position: "left" }}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-text"
                    onClick={() => handleDeleteClick(rowData)}
                    tooltip="Delete network"
                    tooltipOptions={{ position: "left" }}
                />
            </div>
        );
    };

    return (
        <>
            <TableCard title={heading} pagination={true}>
                <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "flex-end" }}>
                    <Button label="Add Network" icon="pi pi-plus" onClick={openCreate} style={{ fontSize: "0.875rem" }} />
                </div>
                <DataTable
                    value={sortedNetworks}
                    showGridlines
                    loading={loading}
                    tableStyle={{ minWidth: "50rem" }}
                    emptyMessage="No networks configured."
                    sortField="network"
                    sortOrder={1}
                    dataKey="_id"
                >
                    <Column body={(_, row) => row.rowIndex + 1} header="#" style={{ width: "3rem" }}></Column>
                    <Column field="network" header="Network Key" style={{ width: "8rem" }}></Column>
                    <Column field="label" header="Display Name" style={{ width: "10rem" }}></Column>
                    <Column field="gasFee" header="Gas Fee" body={gasFeeTemplate} style={{ width: "8rem" }}></Column>
                    <Column header="Supported Currencies" body={currenciesTemplate} style={{ width: "16rem" }}></Column>
                    <Column header="Active" body={activeTemplate} style={{ width: "6rem" }}></Column>
                    <Column header="Actions" body={actionsTemplate} headerStyle={{ width: "8rem" }} bodyStyle={{ textAlign: "center" }}></Column>
                </DataTable>
            </TableCard>

            <Dialog
                header={editing ? `Edit Network: ${editing.label}` : "Add Network"}
                visible={dialogVisible}
                style={{ width: "500px" }}
                onHide={() => { setDialogVisible(false); setEditing(null); }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Network Key (lowercase, no spaces)</label>
                            <InputText
                                value={form.network}
                                onChange={(e) => setForm({ ...form, network: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                                disabled={!!editing}
                                placeholder="e.g. solana"
                                style={{ width: "100%" }}
                            />
                            {editing && <small style={{ opacity: 0.6 }}>Network key cannot be changed after creation.</small>}
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Display Name</label>
                            <InputText
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="e.g. Solana"
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Gas Fee (stablecoin-equivalent)</label>
                        <InputNumber
                            value={form.gasFee}
                            onValueChange={(e) => setForm({ ...form, gasFee: e.value || 0 })}
                            mode="decimal"
                            minFractionDigits={2}
                            maxFractionDigits={4}
                            min={0}
                            style={{ width: "100%" }}
                        />
                    </div>
                    <div>
                        <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Supported Currencies (press Enter to add)</label>
                        <Chips
                            value={form.supportedCurrencies}
                            onChange={(e) => setForm({ ...form, supportedCurrencies: e.value.map((c) => c.toUpperCase()) })}
                            placeholder="USDT, USDC, CAD..."
                            style={{ width: "100%" }}
                        />
                        <small style={{ opacity: 0.6 }}>Currencies the network supports. Users can only send/receive these.</small>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <InputSwitch
                            checked={form.isActive}
                            onChange={(e) => setForm({ ...form, isActive: e.value })}
                            inputId="active-toggle"
                        />
                        <label htmlFor="active-toggle" style={{ fontWeight: 600, fontSize: "0.85rem" }}>Active (visible to users)</label>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: "0.5rem" }}>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => { setDialogVisible(false); setEditing(null); }} />
                        <Button label={editing ? "Save" : "Add"} icon="pi pi-check" onClick={handleSubmit} disabled={!form.network || !form.label} />
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Delete Network"
                visible={deleteDialogVisible}
                style={{ width: "450px" }}
                onHide={() => { setDeleteDialogVisible(false); setDeleteTarget(null); setDeleteConfirmText(""); setDeleteError(""); }}
            >
                {deleteTarget && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <p>
                            Are you sure you want to delete <strong>{deleteTarget.label}</strong> ({deleteTarget.network})?
                        </p>
                        <p style={{ color: "#f39c12", margin: 0 }}>
                            This will hide the network from users immediately. Existing in-flight transactions on this network are not affected.
                        </p>
                        <div>
                            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>Type "DELETE" to confirm</label>
                            <InputText
                                value={deleteConfirmText}
                                onChange={(e) => { setDeleteConfirmText(e.target.value); setDeleteError(""); }}
                                placeholder='Type "DELETE"'
                                style={{ width: "100%" }}
                                className={deleteError ? "p-invalid" : ""}
                            />
                            {deleteError && <small style={{ color: "#e74c3c" }}>{deleteError}</small>}
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                            <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => { setDeleteDialogVisible(false); setDeleteTarget(null); setDeleteConfirmText(""); setDeleteError(""); }} />
                            <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={handleDeleteConfirm} disabled={deleteConfirmText !== "DELETE"} />
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
}
